import { PLATFORM_SITE_NAME } from "./documentHead";
import { resolveAdminPageTitleFromUrl } from "../utils/adminNavigationPaths";

function withSiteName(section) {
  return section ? `${section} | ${PLATFORM_SITE_NAME}` : PLATFORM_SITE_NAME;
}

const ADMIN_HASH_TITLES = {
  "#/weather-intelligence": "Weather Intelligence",
  "#/admin/community-reports": "Community Reports",
  "#/admin/data-health": "Data Health",
  "#/admin/property-import": "Property Import",
  "#/admin/real-estate-sources": "Real Estate Sources",
  "#/admin/real-estate-source-controls": "Source Controls",
  "#/admin/real-estate-source-quality": "Source Quality",
  "#/admin/real-estate-image-health": "Image Health",
  "#/admin/real-estate-image-cache": "Image Cache",
  "#/admin/real-estate-media-quality": "Media Quality",
  "#/admin/geocode-queue": "Geocode Queue",
  "#/admin/real-estate-location-quality": "Location Quality",
  "#/admin/duplicate-review": "Duplicate Review",
  "#/admin/canonical-listings": "Canonical Listings",
  "#/admin/real-estate-review": "Listing Review",
  "#/admin/real-estate-availability-review": "Availability Review",
  "#/admin/users": "Users",
  "#/admin/roles": "Roles",
  "#/admin/audit-log": "Audit Log",
  "#/admin/ingestion-logs": "Ingestion Logs",
  "#/admin/partners": "Partners",
  "#/admin/lead-broker-config": "Lead Broker",
  "#/admin/geo-boundaries": "Geo Boundaries",
  "#/admin/weather-source-controls": "Weather Sources",
  "#/admin/food-source-discovery": "Food Source Discovery",
  "#/admin/vehicle-source-review": "Vehicle Source Review",
  "#/admin/vehicle-lookups": "Vehicle Lookups",
  "#/admin/vehicle-ingestion-runs": "Vehicle Ingestion Runs",
  "#/admin/vehicle-price-events": "Vehicle Price Events",
  "#/admin/vehicle-lifecycle-events": "Vehicle Lifecycle Events",
  "#/admin/vehicle-normalization-issues": "Vehicle Normalization",
  "#/admin/vehicle-duplicate-review": "Vehicle Duplicate Review",
  "#/admin/canonical-vehicles": "Canonical Vehicles",
  "#/admin/vehicle-data-quality": "Vehicle Data Quality",
  "#/admin/vehicle-fair-value": "Vehicle Fair Value",
  "#/admin/vehicle-best-value": "Vehicle Best Value",
  "#/admin/vehicle-new-price-baseline": "New Price Baseline",
  "#/admin/vehicle-depreciation": "Vehicle Depreciation",
  "#/admin/vehicle-predictive-pricing": "Predictive Pricing",
  "#/admin/vehicle-pricing-models": "Pricing Models",
  "#/admin/vehicle-deal-finder-diagnostics": "Deal Finder Diagnostics",
  "#/admin/vehicle-alerts": "Vehicle Alerts",
  "#/admin/automotive-dealers": "AutoMapa Dealers",
  "#/admin/automotive-dealer-review": "Dealer Review Queue",
  "#/admin/automotive-dealer-governance": "Dealer Governance",
  "#/admin/automotive-dealer-coverage": "Dealer Coverage",
};

const PATH_SECTION_TITLES = [
  { prefix: "/transport", title: "Transport" },
  { prefix: "/crm", title: "CRM" },
  { prefix: "/food", title: "Food" },
  { prefix: "/culture", title: "Culture" },
  { prefix: "/community", title: "Community" },
  { prefix: "/gov", title: "Government & NGO" },
  { prefix: "/client-views", title: "Client Views" },
];

const EXACT_PATH_TITLES = {
  "/login": "Sign in — Signal Intelligence Platform",
  "/forgot-password": "Forgot password",
  "/reset-password": "Reset password",
  "/set-password": "Set password",
  "/unauthorized": "Unauthorized",
  "/terms": "Terms of Service",
  "/privacy": "Privacy Policy",
  "/cookies": "Cookie Policy",
  "/email-notifications-policy": "Email Notifications",
  "/broker-partner-terms": "Broker Partner Terms",
  "/account/consent-required": "Consent required",
};

function resolveAdminTitle(hash) {
  if (!hash) {
    return null;
  }

  const normalized = hash.split("?")[0];
  const exact = ADMIN_HASH_TITLES[normalized];
  if (exact) {
    return withSiteName(exact);
  }

  if (normalized.startsWith("#/admin/")) {
    const segment = normalized.replace("#/admin/", "").split("/")[0];
    const label = segment
      .split("-")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
    return withSiteName(label || "Admin");
  }

  return null;
}

/**
 * Resolve platform (non–client-view) document titles from route + admin hash/query.
 * Returns null when a dedicated page SEO hook should own the title.
 */
export function resolveAppPageTitle(pathname, hash = "", search = "") {
  if (!pathname) {
    return null;
  }

  if (pathname.startsWith("/v/automap/dealer")) {
    return "Dealer Portal | AutoMapa";
  }

  if (pathname.startsWith("/v/automap/admin") || pathname.startsWith("/v/automap/partner")) {
    return "Admin | AutoMapa";
  }

  if (pathname.startsWith("/v/")) {
    return null;
  }

  if (EXACT_PATH_TITLES[pathname]) {
    return withSiteName(EXACT_PATH_TITLES[pathname]);
  }

  if (pathname.startsWith("/account")) {
    return withSiteName("Account");
  }

  if (pathname.startsWith("/partner")) {
    return withSiteName("Partner Portal");
  }

  if (pathname.startsWith("/client/")) {
    return withSiteName("Client Portal");
  }

  if (pathname.startsWith("/v/midpoint-media/admin")) {
    return withSiteName("Midpoint Media Operations");
  }

  const adminTitle = resolveAdminTitle(hash);
  if (adminTitle) {
    return adminTitle;
  }

  const adminQueryTitle = resolveAdminPageTitleFromUrl(search);
  if (adminQueryTitle) {
    return withSiteName(adminQueryTitle);
  }

  for (const { prefix, title } of PATH_SECTION_TITLES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return withSiteName(title);
    }
  }

  if (pathname === "/") {
    return withSiteName("Community Intelligence");
  }

  return null;
}
